import { useEffect, useMemo, useRef, useState } from 'react';
import { deriveTypesFromText } from './typesConfigUtils';
import {
  applyRenamePairsToNav,
  computeRenamePairs,
  filterDeletionCandidates,
  isRenameSuppressed,
} from './typesEditUtils';

export type SortMode = 'alpha' | 'manual';

export type UseTypesEditingFlowParams = {
  text: string;
  sortMode: SortMode;
  baseList: string[];
  setNavTypes: (updater: (prev: string[]) => string[]) => void;
  suppressionKeys?: string[];
  getInUseCached: (type: string) => boolean;
  liveCheckInUse?: (type: string, signal?: AbortSignal) => Promise<boolean>;
  isPersisting?: boolean;
  deletedDialogOpen?: boolean;
  onPromptReassignMulti: (
    items: Array<{ type: string; originalPosition: number }>
  ) => void;
  liveDeletionPrompts?: boolean; // if true, show reassign dialog while typing; default false
};

export function useTypesEditingFlow({
  text,
  sortMode,
  baseList,
  setNavTypes,
  suppressionKeys,
  getInUseCached,
  liveCheckInUse,
  isPersisting,
  deletedDialogOpen,
  onPromptReassignMulti,
  liveDeletionPrompts,
}: UseTypesEditingFlowParams) {
  const lastTypesRef = useRef<string[]>(deriveTypesFromText(text, sortMode));
  const pendingRemovalRef = useRef<string | null>(null);
  const [recentlyRenamedFrom, setRecentlyRenamedFrom] = useState<string | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();
    const handle = window.setTimeout(() => {
      const newTypes = deriveTypesFromText(text, sortMode);
      const prevTypes = lastTypesRef.current;
      const removed = prevTypes.filter((t) => !newTypes.includes(t));
      const added = newTypes.filter((t) => !prevTypes.includes(t));

      let renamedFrom: string | string[] | null = null;
      const renamePairs = computeRenamePairs(prevTypes, newTypes);

      if (removed.length === 1 && added.length === 1) {
        setNavTypes((prev) =>
          prev.map((t) => (t === removed[0] ? added[0] : t))
        );
        setRecentlyRenamedFrom((prev) =>
          prev === removed[0] ? prev : removed[0]
        );
        renamedFrom = removed[0];
      }

      if (renamePairs.length > 0) {
        setNavTypes((prev) => applyRenamePairsToNav(prev, renamePairs));
        const renamedList = renamePairs.map((p) => p.from);
        const last = renamedList[renamedList.length - 1] ?? null;
        if (last)
          setRecentlyRenamedFrom((prev) => (prev === last ? prev : last));
        renamedFrom = renamedList;
        if (pendingRemovalRef.current == null && renamePairs.length > 0) {
          pendingRemovalRef.current = renamePairs[0].from;
        }
      }

      setNavTypes((prev) => prev.filter((t) => newTypes.includes(t)));

      const hasAnyRename =
        renamePairs.length > 0 || (removed.length === 1 && added.length === 1);
      const isLikelyBatchRename =
        removed.length >= 1 &&
        added.length >= 1 &&
        (newTypes.length === prevTypes.length ||
          added.length >= removed.length);

      let candidates =
        hasAnyRename || isLikelyBatchRename
          ? []
          : filterDeletionCandidates(
              prevTypes,
              newTypes,
              pendingRemovalRef.current,
              renamedFrom
            );
      const keys = Array.isArray(suppressionKeys) ? suppressionKeys : [];
      candidates = candidates.filter(
        (c) => !keys.some((k) => isRenameSuppressed(k, c))
      );

      if (
        liveDeletionPrompts === true &&
        !isPersisting &&
        !deletedDialogOpen &&
        candidates.length > 0
      ) {
        const deletionsToHandle: Array<{
          type: string;
          originalPosition: number;
        }> = [];
        const asyncChecks: Promise<{
          type: string;
          originalPosition: number;
        } | null>[] = [];

        for (const candidate of candidates) {
          const originalIndex = prevTypes.indexOf(candidate);
          const cached = getInUseCached(candidate);
          if (cached) {
            deletionsToHandle.push({
              type: candidate,
              originalPosition: originalIndex,
            });
          } else if (liveCheckInUse) {
            asyncChecks.push(
              liveCheckInUse(candidate, abortController.signal)
                .then((live) =>
                  live
                    ? { type: candidate, originalPosition: originalIndex }
                    : null
                )
                .catch(() => null)
            );
          }
        }

        if (asyncChecks.length > 0) {
          Promise.all(asyncChecks).then((results) => {
            if (cancelled) return;
            const liveItems = results.filter(
              (r): r is { type: string; originalPosition: number } => r !== null
            );
            const allDeletions = [...deletionsToHandle, ...liveItems];
            if (allDeletions.length > 0) {
              onPromptReassignMulti(allDeletions);
              pendingRemovalRef.current =
                allDeletions[allDeletions.length - 1].type;
            }
          });
        } else if (deletionsToHandle.length > 0) {
          onPromptReassignMulti(deletionsToHandle);
          pendingRemovalRef.current =
            deletionsToHandle[deletionsToHandle.length - 1].type;
        }
      }

      lastTypesRef.current = newTypes;
    }, 150);
    return () => {
      cancelled = true;
      abortController.abort();
      window.clearTimeout(handle);
    };
  }, [
    text,
    sortMode,
    baseList,
    setNavTypes,
    suppressionKeys,
    getInUseCached,
    liveCheckInUse,
    isPersisting,
    deletedDialogOpen,
    onPromptReassignMulti,
    liveDeletionPrompts,
  ]);

  const currentTypes = useMemo(
    () => deriveTypesFromText(text, sortMode),
    [text, sortMode]
  );
  const resetPendingRemoval = () => {
    pendingRemovalRef.current = null;
  };

  return { currentTypes, recentlyRenamedFrom, resetPendingRemoval };
}
